import React, { PureComponent } from 'react';
import Autolinker from 'autolinker';
import { withTranslation, Trans } from 'react-i18next';

import { updateMessage, removeMessage, setMessageReaction, hearMessage } from '../actions/messages';
import { updateDoc } from '../actions/docs';
import { showModal } from '../actions/modal';

import { trackEvent } from '../services/events-tracker-service';
import { isTempId } from '../utils/helpers';
import { deserialize } from '../utils/editor';

import { DiscussionContext } from 'context/DiscussionContext';

import UserName from './user-name';
import UrlPreview from './url-preview';
import Message from './message';
import Tooltip from './tooltip';
import DocContainer from '../containers/doc-container';
import DocCard from './doc-card';
import DropdownBtn from './dropdown-btn';
import InputTextEditor from './input-text-editor';
import TourStarter from './tour-starter';
import TaskCreateCTA from './task-create-cta';
import MessageThreadCTA from './message-thread-cta';
import MessageAttachment from './message-attachment';
import MessageReactions from './message-reactions';
import CanUser from './can-user';
import MessageInvitePeopleCta from './message-invite-people-cta';
import EmojiPicker from './emoji-picker';
import DateTime from 'components/DateTime';
import SimpleAudioPlayer from 'components/SimpleAudioPlayer';

import IconChatBubbleAdd from '../icons/icon-chat-bubble-add';
import IconEmoji from '../icons/icon-emoji';
import IconPin from '../icons/icon-pin';
import IconEmail from '../icons/icon-email';

class MessageRich extends PureComponent {
    static contextType = DiscussionContext;

    static defaultProps = {
        clicksEnabled: true
    }

    state = {
        editing: false,
        controlActionDropdownVisible: false
    };

    loadedAttachments = 0;

    handleSubmit = text => {
        if (text === '') {
            this.delete();
            return;
        }

        if (text === this.props.text) {
            this.abortEdit();
            return;
        }


        this.saveEdit(text);
    }

    handleKeyDown = event => {
        if (event.which === 27) {
            this.abortEdit();
        }
    }

    handleControlActionClick = ({ payload }) => {
        switch (payload.action) {
            case 'edit':
                this.startEdit();
                break;

            case 'delete':
                this.delete();
                break;

            default:
                return null;
        }
    }

    handleBlur = (text, edited) => {
        if (!edited) {
            this.abortEdit();
            return;
        }

        this.handleSubmit(text);
    }

    handleControlActionDropdownShow = () => {
        this.setState({
            controlActionDropdownVisible: true
        });
    }

    handleControlActionDropdownHide = () => {
        this.setState({
            controlActionDropdownVisible: false
        });
    }

    handleCreateThreadClick = () => {
        this.context.onThreadActivate(this.props.id);

        trackEvent('DISCUSSION.THREAD_START');
    }

    handleActivateThreadClick = () => {
        this.context.onThreadActivate(this.props.id);
    }

    handleSendAsEmailClick = () => {
        showModal('SEND_AS_EMAIL', {
            message: {
                id: this.props.id,
                author: this.props.author,
                text: this.props.text,
                html: this.contentPane?.textContent
            },
            users: this.context.users,
            selectedUserIds: this.context.users
                .map(({ id }) => id)
                .filter(id => id !== this.props.author && id !== this.context.currentUserId && !(this.props.seenBy || []).includes(id))
        });
    }

    handleReactionSelect = ({ colons }) => {
        setMessageReaction(this.props.id, colons);

        trackEvent('CHAT.REACTION');
    }

    handleReactionRemove = () => {
        setMessageReaction(this.props.id, null);
    }

    handleDeleteClick = () => {
        this.delete();

        this.setState({
            editing: false
        });
    }

    handlePinMessage = () => {
        if (!this.context?.onPinMessage) {
            return;
        }

        if (this.context.pinnedMessage === this.props.id) {
            this.context.onPinMessage(null);
        } else {
            this.context.onPinMessage(this.props.id);
        }
    }

    handleUrlPreviewRemove = () => {
        updateMessage(this.props.id, {
            hide_link_preview: true
        });
    }

    handlePlay = () => {
        hearMessage(this.props.id);
    }

    startEdit() {
        this.setState({
            editing: true
        }, () => {
            // this.editorCtrl && this.editorCtrl.focus();
            // setTimeout(() => {
            //     this.editorCtrl && this.editorCtrl.focus();
            // }, 500);
            this.context.onEditStart && this.context.onEditStart(this.props.id);
        });

    }

    abortEdit() {
        if (!this.state.editing) {
            return;
        }

        this.setState({
            editing: false
        }, () => {
            this.context.onEditAbort && this.context.onEditAbort();
        });

    }

    saveEdit(text) {
        if (!this.state.editing) {
            return;
        }

        requestAnimationFrame(() => {
            this.setState({
                editing: false
            });
        })

        if (text === this.props.text) {
            return;
        }

        trackEvent('MESSAGE.EDIT');

        updateMessage(this.props.id, { text });
    }

    delete() {
        if (this.props.docAttachment) {
            updateDoc(this.props.docAttachment, {
                archived: true
            });
        }

        removeMessage(this.props.id);

        trackEvent('MESSAGE.REMOVE');
    }

    getTaskName = () => {
        if (!this.contentPane) {
            return this.props.text;
        }

        return this.contentPane.textContent;
    };

    get controlActions() {
        const { t } = this.props;

        let actionDelete = {
            text: t('actions.deleteMessage.title'),
            style: ['danger'],
            payload: {
                action: 'delete'
            },
            prompt: {
                title: t('actions.deleteMessage.titleSimple'),
                text: t('actions.deleteMessage.description'),
                confirmText: t('common:Delete'),
                confirmClassName: 'btn-caution'
            }
        };

        if (this.props.docAttachment) {
            actionDelete = {...actionDelete};
            actionDelete.prompt.title = t('doc:.actions.deleteAlt.title');
            actionDelete.prompt.text = t('doc:.actions.deleteAlt.description');
        }

        if (this.context.controlDeleteText) {
            actionDelete = {...actionDelete};
            actionDelete.text = this.context.controlDeleteText;
        }

        if (this.props.type === 'document') {
            return [
                actionDelete
            ];
        }

        const result = [];

        if (this.props.type !== 'audio' ) {
            result.push({
                text: t('common:Edit'),
                payload: {
                    action: 'edit'
                }
            });
        }

        result.push(actionDelete);

        return result;
    }

    setEditorRef = ctrl => this.editorCtrl = ctrl;
    setContentPane = ref => this.contentPane = ref;

    getControlsElement = () => {
        if ((this.props.error && !this.state.sending) || this.props.removed || this.props.controlsDisabled) {
            return null;
        }

        const { t } = this.props;

        return (
            <div className="message-controls" data-serialize-ignore="true">
                <EmojiPicker onSelect={this.handleReactionSelect}>
                    <div className="emoji-picker-toggle">
                        <Tooltip placement="top" tooltip={ t('Add reaction') } withTooltipHover={false}>
                            <button className="btn btn-square btn-xxsmall btn-simple btn-icon">
                                <IconEmoji size="14px" />
                            </button>
                        </Tooltip>
                    </div>
                </EmojiPicker>

                {
                    this.isSelf && !this.props.botName && !this.props.system ? (
                        <Tooltip placement="top" tooltip={ t('Edit Message')} withTooltipHover={false}>
                            <div className="message-control-item">
                                <DropdownBtn
                                    btnClassName="btn btn-square btn-xxsmall btn-simple btn-icon"
                                    items={this.controlActions}
                                    onDropdownShow={this.handleControlActionDropdownShow}
                                    onDropdownHide={this.handleControlActionDropdownHide}
                                    onItemClick={this.handleControlActionClick}
                                >
                                    <i className="material-icons">edit</i>
                                </DropdownBtn>
                            </div>
                        </Tooltip>
                    ) : null
                }

                {
                    this.context.canSendAsEmail ? (
                        <Tooltip placement="top" tooltip={ t('Send via Email') } withTooltipHover={false}>
                            <button className="btn btn-square btn-xxsmall btn-simple btn-icon" onClick={this.handleSendAsEmailClick}>
                                <IconEmail size="14px" />
                            </button>
                        </Tooltip>
                    ) : null
                }

                <CanUser type="createTask">
                    {
                        this.context.canCreateTask && this.hasText ? <TaskCreateCTA getName={this.getTaskName} projectId={this.context.projectId} /> : null
                    }
                </CanUser>

                {
                    this.context.onThreadActivate ? (
                        <Tooltip placement="top-start" tooltip={ t('Start a Thread') } withTooltipHover={false}>
                            <button className="btn btn-square btn-xxsmall btn-simple btn-icon" onClick={this.handleCreateThreadClick}>
                                <IconChatBubbleAdd size="14px" />
                            </button>
                        </Tooltip>
                    ) : null
                }

                <CanUser type="pinMessage">
                    {
                        this.context.onPinMessage ? (
                            <Tooltip placement="top-start" tooltip={this.context.pinnedMessage === this.props.id ? t('Unpin Message') : t('Pin Message')} withTooltipHover={false}>
                                <button className="btn btn-square btn-xxsmall btn-simple btn-icon" onClick={this.handlePinMessage}>
                                    <IconPin size="14px" />
                                </button>
                            </Tooltip>
                        ) : null
                    }
                </CanUser>
           </div>
        );
    }

    get firstUrl() {
        if (!this.props.text) {
            return null;
        }

        const matches = Autolinker.parse(this.props.text.replace(/(`+(?=[^]+`)[^]+?`+)/g, '%NIFTY_ESC_CODE_REPLACER%'));
        const url = matches && matches[0] && matches[0].url;

        if (url && url.indexOf('http') > 1) {
            return null;
        }

        return url;
    }

    get systemMessageCta() {
        switch (this.props.systemMessageType) {
            case 'tour':
                return <TourStarter />;

            default:
                return null;
        }
    }

    get systemMessageBody() {
        switch (this.props.systemMessageType) {
            case 'hello':
                return <Trans
                    i18nKey="chat:systemMessages.hello"
                    components={{
                        UserName: <UserName id={this.context.currentUserId} />,
                        CanUser: <CanUser type="inviteToTeam" />,
                        MessageInvitePeopleCta: <MessageInvitePeopleCta projectId={this.context.projectId} />
                    }}
                />;

            case 'due_date':
                return <Trans
                    i18nKey="chat:systemMessages.dueDate"
                    values={{
                        date: this.props.systemMessageData.date.iso || this.props.systemMessageData.date
                    }}
                    components={{
                        DateTime: <DateTime format="dateTime" />
                    }}
                />;

            case 'addTaskDeadline':
                return <Trans
                    i18nKey="chat:systemMessages.dueDate"
                    values={{
                        date: this.props.text?.match(/\d{2}\/\d{2}\/\d{4}/)
                    }}
                    components={{
                        DateTime: <DateTime format="fullDate" />
                    }}
                />;

            default:
                return null;
        }
    }

    getMessageBody = htmlContent => {
        return (
            <div className="message-body">
                <div className="message-body-inner">
                    <div className="message-body-main">
                        {
                            this.props.systemMessageType ? (
                                <div className="pane">
                                    { this.systemMessageBody || htmlContent }
                                </div>
                            ) : (
                                this.state.editing ? (
                                    <InputTextEditor
                                        value={deserialize(this.props.text, this.context.users, true)}
                                        provideController={this.setEditorRef}
                                        users={this.context.users}
                                        initialState={this.editorState}
                                        autofocus={true}
                                        allowEmptySubmit={true}
                                        onKeyDown={this.handleKeyDown}
                                        onBlur={this.handleBlur}
                                        onSubmit={this.handleSubmit}
                                        onUserInvite={this.props.onUserInvite}
                                    />
                                ) : this.props.type === 'audio' && this.props.attachments?.[0] ? (
                                    <SimpleAudioPlayer
                                        src={this.props.attachments[0].url}
                                        duration={this.props.audioDuration * 1000}
                                        dimmed={(this.props.heardBy || []).includes(this.context.currentUserId)}
                                        onPlay={this.handlePlay}
                                    />
                                ) : (
                                    <div className="pane" ref={this.setContentPane} onClick={this.handlePaneClick}>
                                        { htmlContent }
                                    </div>
                                )
                            )
                        }

                        { this.attachmentsElement }

                        {
                            !this.props.removed && this.context.urlPreviewEnabled && this.firstUrl && !this.props.hideLinkPreview ? <UrlPreview url={this.firstUrl} isSelf={this.isSelf} onUrlPreviewRemove={this.handleUrlPreviewRemove} /> : null
                        }

                        { this.systemMessageCta }

                        { this.reactionsElement }

                        {
                            !this.props.removed && this.props.replies && this.context.onThreadActivate ? (
                                <MessageThreadCTA id={this.props.id} replies={this.props.replies} repliers={this.props.repliers} onClick={this.handleActivateThreadClick} />
                            ) : null
                        }
                    </div>
                </div>

                <div className="message-controls-helper" data-serialize-ignore="true">
                    <Trans
                        i18nKey="chat:editingKeyboard"
                        components={{
                            delete: <strong className="clickable" onMouseDown={this.handleDeleteClick} />
                        }}
                    />
                </div>
            </div>
        );
    }

    get attachmentsElement() {
        if (this.props.removed) {
            return null;
        }

        if (this.props.type === 'audio') {
            return null;
        }

        if (this.props.type === 'document') {            
            return (
                <DocContainer id={this.props.docAttachment}>
                    <DocCard onClick={this.props.onDocClick} />
                </DocContainer>
            )
        }

        if (this.props.attachments && this.props.attachments.length) {
            return  (
                <div className="message-attachments">
                {
                    this.props.attachments
                        .map(({ id }, index) => <MessageAttachment
                            key={id}
                            id={id}
                            filePreviewPath={this.context.filePreviewPath}
                            siblings={[...this.props.attachments.map(attachment => attachment.id)].reverse()}
                        />)
                }
                </div>
            );
        }

        return null;
    }

    get reactionsElement() {
        if (this.props.removed || !this.props.reactions || !this.props.reactions.length) {
            return null;
        }

        return <MessageReactions reactions={this.props.reactions} onReactionRemove={this.handleReactionRemove} onReactionSelect={this.handleReactionSelect} />;
    }

    get hasText() {
        return this.props.text && this.props.text !== '';
    }

    get isSelf() {
        if (!this.context.currentUserId) {
            return false;
        }

        return this.props.author === this.context.currentUserId;
    }

    get className() {
        const className = ['message'];

        this.isSelf && className.push('self');
        this.props.pending && className.push('pending');
        this.props.noMeta && className.push('no-meta');
        this.props.primary && className.push('primary');
        this.props.system && className.push('message-type-system');
        this.props.firstForDay && className.push('first-for-day');
        this.context.pinnedMessage === this.props.id && className.push('pinned');
        this.context.highlightedMessage === this.props.id && className.push('highlighted');

        (this.state.controlActionDropdownVisible) && className.push('control-action-dropdown-visible');
        this.state.editing && className.push('editing');

        this.messageIsEmoji && className.push('message-emoji-only');
        !this.hasText && className.push('empty-text');

        this.props.error && className.push('error');
        !this.props.removed && this.props.edited && className.push('edited');
        this.props.removed && className.push('removed');

        return className.join(' ');
    }

    componentDidMount() {
        if (!this.context?.registerMessageCtrl) {
            return;
        }

        this.unregisterCtrl = this.context.registerMessageCtrl(this.props.id, {
            edit: () => {
                this.startEdit();
            }
        });
    }

    componentWillUnmount() {
        this.unregisterCtrl && this.unregisterCtrl();
    }


    render() {
        return (
            <Message
                id={this.props.id}
                className={this.className}
                getMessageBody={this.getMessageBody}
                getControlsElement={this.getControlsElement}
                attachments={this.props.attachments}
                placeholder={this.props.placeholder}
                docAttachment={this.props.docAttachment}
                author={this.props.author}
                reactions={this.props.reactions}
                text={this.props.text}
                system={this.props.system}
                botName={this.props.botName}
                botAvatar={this.props.botAvatar}
                clicksEnabled={this.props.clicksEnabled}
                dateFormat={this.context.dateFormat}
                createdAt={this.props.createdAt}
                seenBy={this.props.seenBy}
                heardBy={this.props.heardBy}
                pending={this.props.pending}
                error={this.props.error}
                removed={this.props.removed}
                type={this.props.type}
                url={this.props.url}
                replies={this.props.replies}
                hidden={this.props.hidden}
                highlighted={this.props.highlighted}
                sending={this.props.sending}
                fragment={this.props.fragment}
                currentUserId={this.context.currentUserId}
                discussionId={this.context.discussionId}
                projectId={this.context.projectId}
                gifSize={this.context.gifSize}
                systemAsAction={this.context.systemAsAction}
                canLinkMessage={this.context.canLinkMessage}
                botUseUserName={this.context.botUseUserName}
                pinnedMessage={this.context.pinnedMessage}
                pinnedMessageBy={this.context.pinnedMessageBy}
            />
        )
    }
}


export default withTranslation(['chat', 'common', 'doc'])(MessageRich);
